import React from 'react';
import {
  FiSearch,
  FiPlus,
  FiMinus,
} from 'react-icons/fi';
import useSidebar from './useSidebar';
import { studentNavigationItems, instructorNavigationSections, adminNavigationSections } from './sidebarNavigationItems';
import { SidebarContainer, SidebarHeader, SearchSection, SearchContainer, SearchInput, SearchIcon, SearchResults, SearchResultItem, SearchResultTitle, SearchResultSubtitle, NoResults, NavSection, SectionToggle, SectionToggleIcon, NavList, NavItem, NavLinkStyled, NavIconSlot, MobileOverlay, QuickStats, StatItem } from './Sidebar.styles';

function Sidebar({ isOpen, onClose }) {
  const {
    user,
    stats,
    isSectionCollapsed,
    toggleSection,
    searchTerm,
    searchResults,
    showSearchResults, setShowSearchResults,
    isSearching,
    handleSearchChange,
    handleSearchResultClick,
    handleSearchBlur,
    closeOnMobile,
  } = useSidebar(onClose);

  return (
    <>
      <MobileOverlay
        isOpen={isOpen}
        onClick={onClose}
      />

      <SidebarContainer
        isOpen={isOpen}
      >
        <SidebarHeader>
          <h3>Navigation</h3>
          <p>Access your learning tools</p>
        </SidebarHeader>

        <SearchSection>
          <SearchContainer>
            <SearchIcon>
              <FiSearch size={16} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search the portal..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSearchResults(searchResults.length > 0)}
              onBlur={handleSearchBlur}
            />
            <SearchResults $show={showSearchResults}>
              {isSearching ? (
                <NoResults>Searching...</NoResults>
              ) : searchResults.length > 0 ? (
                searchResults.map((result) => (
                  <SearchResultItem
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <SearchResultTitle>{result.title}</SearchResultTitle>
                    <SearchResultSubtitle>{result.subtitle}</SearchResultSubtitle>
                  </SearchResultItem>
                ))
              ) : searchTerm ? (
                <NoResults>No results found</NoResults>
              ) : null}
            </SearchResults>
          </SearchContainer>
        </SearchSection>

        {(user?.role === 'student'
          ? studentNavigationItems
          : user?.role === 'admin'
            ? adminNavigationSections
            : instructorNavigationSections
        ).map((section, index) => {
          const isCollapsed = isSectionCollapsed(section.section, section.defaultExpanded);
          return (
            <NavSection key={index}>
              <SectionToggle
                type="button"
                onClick={() => toggleSection(section.section, isCollapsed)}
                aria-expanded={!isCollapsed}
                $collapsed={isCollapsed}
              >
                <SectionToggleIcon aria-hidden="true">
                  {isCollapsed ? <FiPlus size={12} /> : <FiMinus size={12} />}
                </SectionToggleIcon>
                {section.section}
              </SectionToggle>
              {!isCollapsed && (
                <NavList>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavItem key={item.path}>
                        <NavLinkStyled
                          to={item.path}
                          onClick={closeOnMobile}
                          end={item.path === '/admin' || item.path === '/admin/reports'} // Only match exact path for admin dashboard / enrollment reports, so nested routes under them don't also light this one up
                        >
                          <NavIconSlot>
                            <Icon className="nav-icon-base" />
                            <Icon className="nav-icon-lines" aria-hidden="true" focusable="false" />
                          </NavIconSlot>
                          {item.label}
                        </NavLinkStyled>
                      </NavItem>
                    );
                  })}
                </NavList>
              )}
            </NavSection>
          );
        })}

        {user?.role === 'student' && (
          <QuickStats>
            <StatItem>
              <span className="label">Active Courses</span>
              <span className="value">{stats.courses}</span>
            </StatItem>
            <StatItem>
              <span className="label">Pending Tasks</span>
              <span className="value">{stats.pending}</span>
            </StatItem>
            <StatItem>
              <span className="label">Completed Tasks</span>
              <span className="value">{stats.completed}</span>
            </StatItem>
          </QuickStats>
        )}
      </SidebarContainer>
    </>
  );
}

export default Sidebar;
